import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import AboutFormLinks from "features/Auth/Onboarding/AboutForm/Links";
import { SocialMediaLinks } from "features/Auth/Onboarding/AboutForm/helpers";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { hasSocialMediaProfile } from "utils/influencer";
import { GAMIFICATION_UNLOCKED_ALL_FEATURES } from "utils/localStorage";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import { getActiveStepForLevelOneFree, hasTaskByType } from "../helpers";
import { OrganizationGamificationTask } from "../schema";
import OnboardingCard from "./OnboardingCard";
import { Icon } from "./shared/icons";
import {
  Confetti,
  ModuleHeader,
  ModulePanel,
  Pill,
  ProgressBar,
  SectionHead,
} from "./shared/primitives";
import { buttonSx, g } from "./shared/tokens";

const UNLOCK_FEATURES = [
  "📇 Direct Contacts",
  "1M+ Brand Contacts",
  "Advanced Features",
];
const _TOTAL_TASKS = 5;

interface Props {
  pitchCount: number | null;
  onRefreshPitchCount: () => void;
  fetchPitchCountLoading?: boolean;
  refreshLoading?: boolean;
  setShowPremiumFeatureUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExperienceFreeLevelOne = ({
  pitchCount,
  onRefreshPitchCount,
  fetchPitchCountLoading,
  refreshLoading,
  setShowPremiumFeatureUnlocked,
}: Props) => {
  const navigate = useNavigate();
  const {
    gamificationProfile,
    currentOrg,
    profile,
    setProfile,
    setGamificationProfile,
  } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, setCurrentTemplateContentType } =
    useContext(OutreachTemplatesContext);
  const { setAlert } = useContext(AlertContext);
  const { plans, setUpgradeDialogSource } = useContext(SubscriptionContext);
  const { getToken } = useAuth();
  const proPrice = plans ? plans[Plans.PRO]?.price : null;
  const proEmailCredits = plans ? plans[Plans.PRO]?.monthly_limit : null;

  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const [saving, setSaving] = useState(false);
  const [templateRefreshLoading, setTemplateRefreshLoading] = useState(false);
  const [indexOfCompletedTask, setIndexOfCompletedTask] = useState<number>(0);
  const { completeTask, refreshGamificationProfile } = useGamification();

  const openPortfolioDialog = () => {
    trackEvent("Gamification - Add Portfolio");
    if (profile) setOnboardingForm(profile);
    setLinks([]);
    setPortfolioDialogOpen(true);
  };

  const handleSavePortfolio = async () => {
    setSaving(true);
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        { ...onboardingForm, page: "Gamification" },
      );
      setProfile(organizationProfile);
      setAlert("Portfolio links saved", "success");
      setPortfolioDialogOpen(false);
      completeTask(OrganizationGamificationTask.add_portfolio);
    } catch (error) {
      setAlert(error, "error");
    } finally {
      setSaving(false);
    }
  };

  const openTemplateDialog = (contentType: TemplateContentType) => {
    trackEvent("Gamification - Create Template", {
      contentType,
    });
    setCreateTemplateDialogOpen(true);
    setCurrentTemplateContentType(contentType);
  };

  const openCampaignDialog = () => {
    trackEvent("Gamification - Create Campaign");
    navigate(`new`);
  };

  const refreshTemplateTask = async (
    taskType: OrganizationGamificationTask,
  ) => {
    try {
      setTemplateRefreshLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/refresh-template-task`,
        "POST",
        {},
        { taskType },
      );
      await refreshGamificationProfile();
    } catch (error) {
      setAlert(error, "error");
    } finally {
      setTemplateRefreshLoading(false);
    }
  };

  const percentage = pitchCount ? Math.round((pitchCount / 10) * 100) : 0;
  const completed = indexOfCompletedTask >= _TOTAL_TASKS;

  useEffect(() => {
    if (gamificationProfile) {
      const index = getActiveStepForLevelOneFree(gamificationProfile);
      setIndexOfCompletedTask(index);
      if (index >= _TOTAL_TASKS) {
        const hasSeenDialog =
          localStorage.getItem(
            `${currentOrg?.id}-${GAMIFICATION_UNLOCKED_ALL_FEATURES}`,
          ) === "true";
        if (!hasSeenDialog) {
          setShowPremiumFeatureUnlocked(true);
        }

        setGamificationProfile((prev) =>
          prev
            ? {
                ...prev,
                unlockPremiumFeature: true,
              }
            : prev,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamificationProfile?.tasksCompleted?.length]);

  const lockedReason = (gate: number) => {
    const remaining = gate - indexOfCompletedTask;
    return `Finish ${remaining} step${remaining === 1 ? "" : "s"} above first`;
  };

  const pitchesCompleted = hasTaskByType(
    gamificationProfile,
    OrganizationGamificationTask.send_pitch,
  );

  const pitchesBody = pitchesCompleted ? undefined : (
    <Box sx={{ mt: "12px", maxWidth: 360 }}>
      {fetchPitchCountLoading ? (
        <>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}
          >
            <Skeleton width={70} height={18} />
            <Skeleton width={100} height={18} />
          </Box>
          <Skeleton
            variant="rounded"
            height={8}
            sx={{ borderRadius: "999px" }}
          />
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "8px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: g.text2 }}>
                Progress
              </Typography>
              <IconButton
                size="small"
                onClick={onRefreshPitchCount}
                disabled={refreshLoading}
              >
                {refreshLoading ? (
                  <CircularProgress size={12} />
                ) : (
                  <Box
                    component="i"
                    className="fa-regular fa-rotate-right"
                    sx={{ fontSize: 12 }}
                  />
                )}
              </IconButton>
            </Box>
            {(pitchCount || 0) < 10 ? (
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: g.primary }}
              >
                {pitchCount || 0} / 10 emails sent
              </Typography>
            ) : (
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: g.successText }}
              >
                Completed
              </Typography>
            )}
          </Box>
          <ProgressBar value={(pitchCount || 0) < 10 ? percentage : 100} />
        </>
      )}
    </Box>
  );

  if (completed) {
    return (
      <ModulePanel>
        <ModuleHeader
          title="You're ready to pitch 🚀"
          sub="Onboarding is done. Direct contacts, 1M+ brand contacts, and advanced features are now live in your account."
        />

        {/* Celebration hero */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${g.primary} 0%, #1c5340 100%)`,
            color: "#fff",
            borderRadius: g.radiusLg,
            p: "24px 26px",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            gap: "18px",
            flexWrap: "wrap",
          }}
        >
          <Confetti count={36} />
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "999px",
              background: g.yellow,
              color: g.primary,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Icon.Unlock size={22} />
          </Box>
          <Box sx={{ position: "relative", flex: 1, minWidth: 200 }}>
            <SectionHead sx={{ color: g.yellow }}>
              Onboarding complete
            </SectionHead>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 800,
                mt: "4px",
                letterSpacing: "-0.015em",
                color: "white",
              }}
            >
              All of Bento is yours, {currentOrg?.name}!
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "rgba(255,255,255,0.78)", mt: "4px" }}
            >
              Direct contacts, 1M+ brand contacts, and advanced features are
              live in your account.
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: "flex-end",
            }}
          >
            {UNLOCK_FEATURES.map((label) => (
              <Pill
                key={label}
                icon={<Icon.Check size={12} />}
                sx={{ background: "rgba(255,255,255,0.10)", color: "#fff" }}
              >
                {label}
              </Pill>
            ))}
          </Box>
        </Box>

        {/* Upgrade upsell */}
        <Box
          sx={{
            mt: "14px",
            p: "16px 20px",
            background: g.cardBg,
            border: `1px solid ${g.border}`,
            borderRadius: g.radiusMd,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: g.text }}>
              Want more credits?
            </Typography>
            <Typography sx={{ fontSize: 14, color: g.text2 }}>
              Get{" "}
              <Box component="span" sx={{ color: g.primary, fontWeight: 700 }}>
                {proEmailCredits} emails / month
              </Box>{" "}
              + unlimited features
            </Typography>
          </Box>
          <Button
            sx={{ ...buttonSx.base, ...buttonSx.primary, flexShrink: 0 }}
            onClick={() =>
              setUpgradeDialogSource(
                UpgradeDialogSource.gamification_more_emails,
              )
            }
          >
            Upgrade
          </Button>
        </Box>
      </ModulePanel>
    );
  }

  return (
    <ModulePanel>
      <ModuleHeader
        title="Unlock all features on Bento"
        sub="Complete these action items to unlock all features on Bento"
        right={
          indexOfCompletedTask === 0 && (
            <Pill variant="yellow" icon={<Icon.Sparkle size={12} />}>
              Complete Onboarding
            </Pill>
          )
        }
      />

      {/* Unlock progress banner */}
      <Box
        sx={{
          background: g.primaryTint,
          border: `1px solid ${g.primaryTintBorder}`,
          borderRadius: g.radiusLg,
          p: "18px 22px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: g.radiusSm,
                background: g.primary,
                color: g.yellow,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Icon.Trophy size={16} />
            </Box>
            <SectionHead sx={{ color: g.primary }}>
              Your unlock progress
            </SectionHead>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "6px",
              color: g.primary,
            }}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
              {indexOfCompletedTask}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 600, opacity: 0.55 }}>
              / {_TOTAL_TASKS}
            </Typography>
          </Box>
        </Box>

        <ProgressBar value={(indexOfCompletedTask / _TOTAL_TASKS) * 100} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: g.primary, mr: "4px" }}
          >
            UNLOCKS:
          </Typography>
          {UNLOCK_FEATURES.map((label) => (
            <Pill key={label} sx={{ background: "#fff" }}>
              {label}
            </Pill>
          ))}
          {proPrice && (
            <Typography sx={{ ml: "auto", fontSize: 12, color: g.primary }}>
              or skip by{" "}
              <Box
                component="a"
                onClick={() =>
                  setUpgradeDialogSource(
                    UpgradeDialogSource.gamification_bypass,
                  )
                }
                sx={{
                  cursor: "pointer",
                  color: g.primary,
                  fontWeight: 700,
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                }}
              >
                upgrading
              </Box>
            </Typography>
          )}
        </Box>
      </Box>

      <SectionHead sx={{ mt: "22px", mb: "10px" }}>
        Onboarding steps
      </SectionHead>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <OnboardingCard
          icon={<Icon.Link size={18} />}
          title="Add a Link"
          estimate="<1"
          helper="Social link, website, portfolio, company page — anywhere brands can see your work."
          buttonTitle="Add a link"
          onClick={openPortfolioDialog}
          completed={hasTaskByType(
            gamificationProfile,
            OrganizationGamificationTask.add_portfolio,
          )}
        />

        <OnboardingCard
          icon={<Icon.Doc size={18} />}
          title="Create Your Pitch Template"
          estimate="5-10"
          helper="Write the email Bento sends to brands. Strong templates roughly 3x your reply rate."
          buttonTitle="Create pitch template"
          onClick={() => openTemplateDialog(TemplateContentType.OUTREACH)}
          completed={hasTaskByType(
            gamificationProfile,
            OrganizationGamificationTask.add_template,
          )}
          locked={indexOfCompletedTask < 1}
          lockedReason={lockedReason(1)}
          onRefresh={() =>
            refreshTemplateTask(OrganizationGamificationTask.add_template)
          }
          refreshLoading={templateRefreshLoading}
        />

        <OnboardingCard
          icon={<Icon.Send size={18} />}
          title="Create Follow-Up Template"
          estimate="2-3"
          helper="Most replies come from follow-ups — set this up once and Bento sends them automatically."
          buttonTitle="Create follow-up"
          onClick={() => openTemplateDialog(TemplateContentType.FOLLOWUP)}
          completed={hasTaskByType(
            gamificationProfile,
            OrganizationGamificationTask.add_followup,
          )}
          locked={indexOfCompletedTask < 2}
          lockedReason={lockedReason(2)}
          onRefresh={() =>
            refreshTemplateTask(OrganizationGamificationTask.add_followup)
          }
          refreshLoading={templateRefreshLoading}
        />

        <OnboardingCard
          icon={<Icon.Plane size={18} />}
          title="Send 10 Brand Pitches"
          estimate="1-2"
          helper="Browse brands matched to your niche and send your first 10 pitches to finish onboarding."
          buttonTitle="Send pitches"
          onClick={openCampaignDialog}
          completed={pitchesCompleted}
          locked={indexOfCompletedTask < 3}
          lockedReason={lockedReason(3)}
          body={pitchesBody}
        />

        <OnboardingCard
          icon={<Icon.Send size={18} />}
          title="Add a Follow-Up to a Pitch"
          estimate="<1"
          helper="Pick one of the pitches you've sent and queue a follow-up — Bento sends it automatically if they don't reply."
          buttonTitle="Add a follow-up"
          onClick={() => navigate(`/${routes.inbox}`)}
          completed={hasTaskByType(
            gamificationProfile,
            OrganizationGamificationTask.send_followup,
          )}
          locked={indexOfCompletedTask < 4}
          lockedReason={lockedReason(4)}
        />
      </Box>

      <Dialog
        open={portfolioDialogOpen}
        onClose={() => setPortfolioDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Portfolio Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            loading={saving}
            variant="contained"
            onClick={handleSavePortfolio}
            disableElevation
            disabled={!hasSocialMediaProfile(onboardingForm)}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </ModulePanel>
  );
};

export default ExperienceFreeLevelOne;
